<script setup lang="ts">
import { computed, useAttrs } from "vue";
import UndefinedIcon from "./icons/UndefinedIcon.vue";

import festivalIcon from "~/assets/icons/festivalIcon.svg?raw";
import nightlifeIcon from "~/assets/icons/nightlifeIcon.svg?raw";
import restaurantIcon from "~/assets/icons/restaurantIcon.svg?raw";
import eventVenueIcon from "~/assets/icons/eventVenueIcon.svg?raw";
import artGaleryIcon from "~/assets/icons/artGaleryIcon.svg?raw";
import networkingIcon from "~/assets/icons/networkingIcon.svg?raw";
import wellnessIcon from "~/assets/icons/wellnessIcon.svg?raw";
import gamingIcon from "~/assets/icons/gamingIcon.svg?raw";

defineOptions({ name: "AppIcon" });

const props = defineProps<{ name?: string; color?: string }>();
const attrs = useAttrs();

function sanitizeSvg(raw: string) {
  return raw
    .replace(/width="[^"]+"/gi, "")
    .replace(/height="[^"]+"/gi, "")
    .replace(/fill="[^" ]+"/gi, 'fill="currentColor"')
    .replace(/stroke="[^" ]+"/gi, 'stroke="currentColor"')
    .replace(/<svg/gi, '<svg focusable="false" aria-hidden="true"')
    .trim();
}

const svgRegistry: Record<string, string> = {
  "Music & Festivals": sanitizeSvg(festivalIcon),
  "Business & Learning": sanitizeSvg(networkingIcon),
  "Events & Community": sanitizeSvg(eventVenueIcon),
  "Sports & Wellness": sanitizeSvg(wellnessIcon),
  "Nightlife & Drinks": sanitizeSvg(nightlifeIcon),
  "Food & Dining": sanitizeSvg(restaurantIcon),
  "Arts & Culture": sanitizeSvg(artGaleryIcon),
  "Games & Competition": sanitizeSvg(gamingIcon),
};

const iconAliases: Record<string, string> = {
  stadium: "Music & Festivals",
  businesscenter: "Business & Learning",
  business_center: "Business & Learning",
  eventseat: "Events & Community",
  event_seat: "Events & Community",
  fitnesscenter: "Sports & Wellness",
  fitness_center: "Sports & Wellness",
  nightlife: "Nightlife & Drinks",
  restaurant: "Food & Dining",
  palette: "Arts & Culture",
  gamescompetition: "Games & Competition",

  concerticon: "Music & Festivals",
  festivalicon: "Music & Festivals",
  theatericon: "Arts & Culture",
  comedyicon: "Events & Community",
  nightlifeicon: "Nightlife & Drinks",
  exhibitionicon: "Arts & Culture",
  conferenceicon: "Business & Learning",
  workshopicon: "Business & Learning",
  sportseventicon: "Sports & Wellness",
  foodicon: "Food & Dining",
  nightclubicon: "Nightlife & Drinks",
  baricon: "Nightlife & Drinks",
  cafeicon: "Food & Dining",
  eventvenueicon: "Events & Community",
  concertvenueicon: "Music & Festivals",
  artgaleryicon: "Arts & Culture",
  artgalleryicon: "Arts & Culture",
  hotelicon: "Events & Community",
  sportsvenueicon: "Sports & Wellness",
  communitygatheringicon: "Events & Community",
  networkingicon: "Business & Learning",
  wellnessicon: "Sports & Wellness",
  culturaleventicon: "Arts & Culture",

  concert: "Music & Festivals",
  festival: "Music & Festivals",
  theaterperformance: "Arts & Culture",
  comedyshow: "Events & Community",
  nightlifeparties: "Nightlife & Drinks",
  exhibition: "Arts & Culture",
  conference: "Business & Learning",
  workshop: "Business & Learning",
  sportsevent: "Sports & Wellness",
  fooddrinkevent: "Food & Dining",
  nightclub: "Nightlife & Drinks",
  barlounge: "Nightlife & Drinks",
  cafe: "Food & Dining",
  eventvenue: "Events & Community",
  concertvenue: "Music & Festivals",
  theater: "Arts & Culture",
  artgallery: "Arts & Culture",
  hotel: "Events & Community",
  sportsvenue: "Sports & Wellness",
  communitygathering: "Events & Community",
  networkingbusiness: "Business & Learning",
  wellnessfitness: "Sports & Wellness",
  culturalevent: "Arts & Culture",
};

function normalizeIconName(name: string): string {
  if (svgRegistry[name]) return name;
  const raw = String(name).trim();

  const compact = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

  // Support path/file-like values coming from API, e.g.
  // /icons/sportsEventIcon.svg or sports_event_icon.svg
  const lastSegment = raw.split("/").pop() ?? raw;
  const basename = lastSegment.replace(/\.[a-z0-9]+$/i, "");
  const compactBase = basename.toLowerCase().replace(/[^a-z0-9]+/g, "");

  const candidates = [
    compact,
    compactBase,
    compact.replace(/svg$/i, ""),
    compact.replace(/icon$/i, ""),
    compact.replace(/iconsvg$/i, ""),
    compactBase.replace(/svg$/i, ""),
    compactBase.replace(/icon$/i, ""),
    compactBase.replace(/iconsvg$/i, ""),
  ];

  for (const c of candidates) {
    const canonical = iconAliases[c];
    if (canonical && svgRegistry[canonical]) return canonical;
  }

  return name;
}

const iconHtml = computed(() => {
  if (!props.name) return null;
  const normalized = normalizeIconName(props.name);
  return svgRegistry[normalized] ?? null;
});

const isHtml = computed(() => iconHtml.value !== null);

const mergedAttrs = computed(() => {
  const incomingClass = (attrs as Record<string, unknown>).class as
    | string
    | undefined;
  const colorClass = props.color ?? "";
  const classList = [incomingClass, colorClass].filter(Boolean).join(" ");
  const out = { ...attrs } as Record<string, unknown>;
  if (classList) {
    out.class = classList;
  }
  return out;
});
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <span v-if="isHtml" v-bind="mergedAttrs" v-html="iconHtml" />
  <component :is="UndefinedIcon" v-else v-bind="mergedAttrs" />
</template>
